import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Button, Popconfirm, Select, Space, Table, Tag, Typography, message } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import {
  useApporteursApi, type TApporteur, type TAssociationEcole, type TOrganisation,
} from '../../../api/paiement/apporteurs';

// Écoles associées à un animateur (A3) : les élèves rattachés à l'école APRÈS
// l'association lui sont attribués, sauf ceux qui le sont déjà. Une école n'a
// qu'un animateur à la fois ; la dissociation ne retire aucun élève.

const erreurServeur = (err: unknown): string | undefined =>
  (err as { response?: { data?: { error?: string } } })?.response?.data?.error;

export const nomAnimateur = (a?: TApporteur) =>
  a ? [a.prenom, a.nom].filter(Boolean).join(' ') || a.login || `#${a.id}` : '';

const EcolesAssociees: React.FC<{ apporteurs: TApporteur[]; peutGerer: boolean }> = ({ apporteurs, peutGerer }) => {
  const { t } = useTranslation();
  const api = useApporteursApi();
  const apiRef = useRef(api);
  apiRef.current = api;

  const [ecoles, setEcoles] = useState<TAssociationEcole[]>([]);
  const [loading, setLoading] = useState(false);
  const [organisations, setOrganisations] = useState<TOrganisation[]>([]);
  const [recherche, setRecherche] = useState('');
  const [animateur, setAnimateur] = useState<number | undefined>();
  const [organisation, setOrganisation] = useState<number | undefined>();
  const [enCours, setEnCours] = useState(false);

  const charger = useCallback(async () => {
    setLoading(true);
    try {
      setEcoles(await apiRef.current.ecoles());
    } catch {
      message.error(t('apporteurs.ecoles.chargementErreur', 'Échec du chargement des écoles associées'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    charger();
  }, [charger]);

  // Recherche d'établissements, avec un délai pour ne pas interroger à chaque
  // frappe.
  useEffect(() => {
    if (!peutGerer) return;
    let vivant = true;
    const minuteur = window.setTimeout(() => {
      apiRef.current
        .organisations(recherche)
        .then((o) => vivant && setOrganisations(o))
        .catch(() => vivant && setOrganisations([]));
    }, 300);
    return () => {
      vivant = false;
      window.clearTimeout(minuteur);
    };
  }, [recherche, peutGerer]);

  const parId = new Map(apporteurs.map((a) => [a.id, a]));

  const associer = async () => {
    if (!animateur || !organisation) return;
    setEnCours(true);
    try {
      await api.associerEcole(animateur, organisation);
      message.success(t('apporteurs.ecoles.associee', 'École associée'));
      setOrganisation(undefined);
      setRecherche('');
      charger();
    } catch (err) {
      message.error(erreurServeur(err) || t('apporteurs.ecoles.associationErreur', "Échec de l'association"));
    } finally {
      setEnCours(false);
    }
  };

  const dissocier = async (e: TAssociationEcole) => {
    try {
      await api.dissocierEcole(e.id);
      message.success(t('apporteurs.ecoles.dissociee', 'École dissociée'));
      charger();
    } catch (err) {
      message.error(erreurServeur(err) || t('apporteurs.ecoles.dissociationErreur', 'Échec de la dissociation'));
    }
  };

  const colonnes: ColumnsType<TAssociationEcole> = [
    { title: t('apporteurs.ecoles.ecole', 'École'), dataIndex: 'nom', key: 'nom' },
    {
      title: t('apporteurs.admin.animateur', 'Animateur'),
      key: 'animateur',
      render: (_, e) => nomAnimateur(parId.get(e.apporteur_id)) || `#${e.apporteur_id}`,
    },
    {
      title: t('apporteurs.ecoles.periode', 'Période'),
      key: 'periode',
      render: (_, e) => (
        <Space>
          <span>{dayjs(e.debut).format('DD/MM/YYYY')}</span>
          <span>→</span>
          {e.fin ? <span>{dayjs(e.fin).format('DD/MM/YYYY')}</span> : <Tag color="green">{t('apporteurs.ecoles.enCours', 'en cours')}</Tag>}
        </Space>
      ),
    },
    { title: t('apporteurs.ecoles.eleves', 'Élèves attribués'), dataIndex: 'eleves_attribues', key: 'eleves' },
    {
      title: '',
      key: 'actions',
      render: (_, e) => peutGerer && !e.fin ? (
        <Popconfirm
          title={t('apporteurs.ecoles.confirmerDissociation', "Dissocier cette école ? Les élèves déjà attribués le restent.")}
          onConfirm={() => dissocier(e)}
        >
          <Button danger>{t('apporteurs.ecoles.dissocier', 'Dissocier')}</Button>
        </Popconfirm>
      ) : null,
    },
  ];

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <Alert
        type="info"
        showIcon
        message={t('apporteurs.ecoles.explication',
          "Les élèves rattachés à l'école après l'association sont attribués à l'animateur, sauf ceux qui le sont déjà. Une école n'a qu'un animateur à la fois ; la dissociation ne retire aucun élève.")}
      />
      {peutGerer && (
        <Space wrap>
          <Select
            style={{ minWidth: 220 }}
            placeholder={t('apporteurs.admin.animateur', 'Animateur')}
            value={animateur}
            onChange={setAnimateur}
            options={apporteurs.map((a) => ({ value: a.id, label: nomAnimateur(a) }))}
          />
          <Select
            style={{ minWidth: 280 }}
            showSearch
            filterOption={false}
            placeholder={t('apporteurs.ecoles.rechercher', 'Rechercher un établissement')}
            value={organisation}
            onSearch={setRecherche}
            onChange={setOrganisation}
            options={organisations.map((o) => ({
              value: o.id,
              disabled: o.apporteur_id !== undefined && o.apporteur_id !== null,
              label: o.apporteur_id
                ? `${o.nom} — ${t('apporteurs.ecoles.dejaAssociee', 'déjà associée')}`
                : o.nom,
            }))}
          />
          <Button type="primary" onClick={associer} loading={enCours} disabled={!animateur || !organisation}>
            {t('apporteurs.ecoles.associer', 'Associer')}
          </Button>
        </Space>
      )}
      <Table rowKey="id" columns={colonnes} dataSource={ecoles} loading={loading} scroll={{ x: true }} />
      <Typography.Text type="secondary">
        {t('apporteurs.ecoles.note', 'Le nombre d’élèves attribués compte les attributions faites par cette association.')}
      </Typography.Text>
    </Space>
  );
};

export default EcolesAssociees;
